/**
 * AJ Lead Capture — shared offer "card" markup builder.
 *
 * Builds the inner card (headline, subhead, body, form) reused by the modal,
 * toast, and inline surfaces. Pure DOM construction (no innerHTML with offer
 * copy) so user-entered text can never inject markup.
 *
 * @module markup
 */

let uid = 0;

/**
 * Create an element with optional class, text, and attributes.
 *
 * @param {string} tag                Tag name.
 * @param {string} [className]        Class name.
 * @param {Object} [opts]             Options.
 * @param {string} [opts.text]        textContent.
 * @param {Object} [opts.attrs]       Attribute map.
 * @returns {HTMLElement} The element.
 */
function el(tag, className, opts = {}) {
  const node = document.createElement(tag);
  if (className) {
    node.className = className;
  }
  if (opts.text != null) {
    node.textContent = opts.text;
  }
  if (opts.attrs) {
    Object.keys(opts.attrs).forEach((k) => node.setAttribute(k, opts.attrs[k]));
  }
  return node;
}

/**
 * Append body copy as paragraphs split on blank lines.
 *
 * @param {HTMLElement} parent Container.
 * @param {string}      body   Raw multi-line text.
 * @returns {void}
 */
function appendBody(parent, body) {
  const text = (body || '').trim();
  if (!text) {
    return;
  }
  const wrap = el('div', 'aj-lo-card__body');
  text.split(/\n{2,}/).forEach((para) => {
    const clean = para.trim();
    if (clean) {
      wrap.appendChild(el('p', null, { text: clean }));
    }
  });
  parent.appendChild(wrap);
}

/**
 * Build the offer card.
 *
 * @param {Object}   offer              Offer config (copy + id).
 * @param {Object}   [opts]             Options.
 * @param {boolean}  [opts.showClose]   Render a close button.
 * @param {Function} [opts.onClose]     Close handler.
 * @param {Function} [opts.onSubmit]    Async submit handler(email) -> Promise.
 * @returns {{root: HTMLElement, labelId: string, focusTarget: HTMLElement}}
 *   The card root, the headline id (for aria-labelledby), and the element to
 *   focus first.
 */
export function buildCard(offer, opts = {}) {
  uid += 1;
  const labelId = `aj-lo-headline-${offer.id || 'x'}-${uid}`;
  const inputId = `aj-lo-email-${offer.id || 'x'}-${uid}`;
  const nameId = `aj-lo-name-${offer.id || 'x'}-${uid}`;
  const copy = offer.copy || {};
  const collectName = copy.collect_name !== false && copy.collect_name !== 0;

  const root = el('div', 'aj-lo-card');

  if (opts.showClose) {
    const close = el('button', 'aj-lo-card__close', {
      text: '×',
      attrs: { type: 'button', 'aria-label': 'Close' },
    });
    close.addEventListener('click', () => opts.onClose && opts.onClose());
    root.appendChild(close);
  }

  if (copy.headline) {
    root.appendChild(el('h2', 'aj-lo-card__headline', {
      text: copy.headline,
      attrs: { id: labelId },
    }));
  }

  if (copy.subhead) {
    root.appendChild(el('p', 'aj-lo-card__subhead', { text: copy.subhead }));
  }

  appendBody(root, copy.body);

  // Form.
  const form = el('form', 'aj-lo-card__form', { attrs: { novalidate: 'novalidate' } });

  // Optional first-name field (rendered above email).
  let nameLabel = null;
  let nameInput = null;
  if (collectName) {
    nameLabel = el('label', 'aj-lo-card__label', {
      text: 'First name',
      attrs: { for: nameId },
    });
    nameInput = el('input', 'aj-lo-card__input', {
      attrs: {
        id: nameId,
        type: 'text',
        name: 'name',
        autocomplete: 'given-name',
        required: 'required',
        placeholder: copy.name_placeholder || 'First name',
      },
    });
  }

  const label = el('label', 'aj-lo-card__label', {
    text: 'Email address',
    attrs: { for: inputId },
  });

  const input = el('input', 'aj-lo-card__input', {
    attrs: {
      id: inputId,
      type: 'email',
      name: 'email',
      autocomplete: 'email',
      inputmode: 'email',
      required: 'required',
      placeholder: copy.email_placeholder || 'Your email address',
    },
  });

  // Honeypot — bots fill it; humans never see it.
  const honeypot = el('input', 'aj-lo-hp', {
    attrs: {
      type: 'text',
      name: 'email_confirm',
      tabindex: '-1',
      autocomplete: 'off',
      'aria-hidden': 'true',
    },
  });

  const cta = el('button', 'aj-lo-card__cta', {
    text: copy.cta_label || 'Subscribe',
    attrs: { type: 'submit' },
  });

  const msg = el('p', 'aj-lo-card__msg', { attrs: { role: 'status', 'aria-live': 'polite' } });

  if (collectName) {
    form.append(nameLabel, nameInput);
  }
  form.append(label, input, honeypot, cta, msg);
  root.appendChild(form);

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    msg.className = 'aj-lo-card__msg';
    msg.textContent = '';

    // Honeypot tripped — pretend success, do nothing.
    if (honeypot.value) {
      return;
    }

    if (collectName && !nameInput.value.trim()) {
      msg.classList.add('aj-lo-card__msg--error');
      msg.textContent = 'Please enter your first name.';
      nameInput.focus();
      return;
    }

    if (!input.checkValidity() || !input.value) {
      msg.classList.add('aj-lo-card__msg--error');
      msg.textContent = 'Please enter a valid email address.';
      input.focus();
      return;
    }

    if (!opts.onSubmit) {
      return;
    }

    cta.disabled = true;
    Promise.resolve(opts.onSubmit({ email: input.value, name: nameInput ? nameInput.value.trim() : '' }))
      .then((result) => {
        msg.classList.add('aj-lo-card__msg--success');
        msg.textContent = (result && result.message) || copy.success_message || 'Thanks! Please check your inbox.';
        form.querySelectorAll('input').forEach((i) => { i.disabled = true; });
      })
      .catch((err) => {
        // Nothing was saved, so never show a success state. A rejected
        // address (422) gets the server's message so they can fix it;
        // anything else gets a plain "try again" with no backend detail.
        const badEmail = err && err.status === 422;
        cta.disabled = false;
        msg.classList.add('aj-lo-card__msg--error');
        msg.textContent = badEmail && err.message ? err.message : 'Something went wrong. Please try again.';
        if (badEmail) {
          input.focus();
        }
      });
  });

  return { root, labelId, focusTarget: collectName ? nameInput : input };
}
